import React from 'react';
import {
  Activity,
  ArrowRight,
  Bell,
  Building2,
  Calendar,
  ChevronRight,
  PenSquare,
  Plus,
  Send,
  Vote,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getStoredUser } from '@shared/auth/session';
import { notificationsAPI } from '../lib/api';
import { Avatar, Button, CountdownRing, SectionLabel } from '../components/ui';
import { ELECTION } from '../lib/election';
import { daysUntil, fmtDateMed, initialsOf } from '../lib/utils';
import {
  useCitizenWorkspaceId,
  useNews,
  useCitizenOutreachItems,
  useProfile,
  useWeekCalendar,
  type CalendarEvent,
  type OutreachItem,
} from '../hooks/useCitizenData';
import ccLogo from '../assets/cc-logo.png';
import paulaAvatar from '../assets/paula.png';

interface FeedItem {
  key: string;
  icon: React.ReactNode;
  tone: 'cool' | 'primary' | 'sage';
  label: string;
  title: string;
  sub: string;
  sort: number;
  onTap: () => void;
}

export default function HomeScreen() {
  const navigate = useNavigate();
  const { data: profile } = useProfile();
  const workspaceId = useCitizenWorkspaceId();
  const { data: outreachItems } = useCitizenOutreachItems(workspaceId);
  const { data: weekEvents } = useWeekCalendar();
  const { data: news } = useNews();

  const storedUser = getStoredUser<{ firstName?: string; displayName?: string }>();
  const firstName = profile?.firstName || storedUser?.firstName || 'there';

  const { data: unreadCount } = useQuery(
    ['citizen-unread-count'],
    async () => {
      const { data } = await notificationsAPI.getUnreadCount();
      return data.data?.unreadCount ?? 0;
    },
    { staleTime: 60 * 1000 }
  );

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  })();

  const gDays = daysUntil(ELECTION.general);
  const pDays = daysUntil(ELECTION.primary);
  const next =
    pDays > 0
      ? { label: 'You vote in the primary on', date: ELECTION.primary, days: pDays }
      : { label: 'You vote on', date: ELECTION.general, days: gDays };

  const activeOutreach = (outreachItems || []).filter(
    (o) => (o.status || '').toLowerCase() === 'active' || (o.status || '').toLowerCase() === 'launched'
  );

  // Featured spot: an outreach request closing within 5 days, if any
  const urgentReq = activeOutreach.find((o) => {
    const expires = o.closes_at || o.end_date;
    return expires ? daysUntil(expires) <= 5 : false;
  });

  const feed = buildFeed({ outreach: activeOutreach, events: weekEvents || [], navigate });

  const priorities = profile?.interests || [];

  return (
    <div className="flex flex-col screen-enter">
      {/* Header */}
      <div
        className="app-header flex items-center justify-between px-4 sticky top-0 z-30 backdrop-blur"
        style={{ background: 'color-mix(in oklab, var(--background) 88%, transparent)' }}
      >
        <img src={ccLogo} alt="Capitol Commons" className="h-7 w-auto" style={{ mixBlendMode: 'multiply' }} />
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => navigate('/citizen/notifications')} className="relative">
            <Bell className="w-5 h-5" />
            {!!unreadCount && (
              <span className="absolute top-1.5 right-2 w-2 h-2 rounded-full" style={{ background: 'var(--gold)' }} />
            )}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => navigate('/citizen/profile')}>
            <Avatar initials={initialsOf(profile?.displayName || storedUser?.displayName || firstName)} size={28} tone="accent" />
          </Button>
        </div>
      </div>

      {/* 1. Hero: greeting + countdown */}
      <div
        className="relative mx-4 mt-1 rounded-[28px] p-5 text-white overflow-hidden lift-2"
        style={{
          background: 'linear-gradient(135deg, var(--hero-deep-3) 0%, var(--hero-deep) 60%, var(--hero-deep-2) 100%)',
        }}
      >
        <div className="absolute inset-0 topo-overlay pointer-events-none" />
        <div className="relative flex items-center justify-between gap-3">
          <div className="space-y-1.5">
            <p className="text-[11px] uppercase tracking-widest text-white/70 font-semibold">{greeting},</p>
            <h1 className="text-[28px] font-extrabold tracking-tight leading-tight">{firstName}.</h1>
            <p className="text-sm text-white/85 leading-snug mt-2">
              {next.label} <b className="text-white">{fmtDateMed(next.date)}.</b>
            </p>
          </div>
          <CountdownRing days={next.days} total={365} size={104} stroke={9} label="days" />
        </div>
      </div>

      {/* 2. Quick actions */}
      <div className="px-4 pt-5">
        <div className="grid grid-cols-4 gap-2 pt-1">
          <CircleTile
            tone="accent"
            icon={<Activity className="w-6 h-6" />}
            label="News"
            badge={news?.length ? String(Math.min(news.length, 9)) : undefined}
            onClick={() => navigate('/citizen/briefing')}
          />
          <CircleTile tone="dark" icon={<Vote className="w-6 h-6" />} label="Ballot" onClick={() => navigate('/citizen/ballot')} />
          <CircleTile tone="cool" avatar={paulaAvatar} label="Paula" onClick={() => navigate('/citizen/chat')} />
          <CircleTile
            tone="plum"
            icon={<Send className="w-6 h-6" />}
            label="Contact"
            onClick={() => navigate('/citizen/calls')}
          />
        </div>
      </div>

      {/* 3. Featured: urgent request callout */}
      {urgentReq && (
        <div className="px-4 pt-5">
          <FeaturedRequest r={urgentReq} onClick={() => navigate(`/citizen/requests/${urgentReq.id}`)} />
        </div>
      )}

      {/* 4. What's happening — unified feed */}
      <div className="px-4 pt-6">
        <SectionLabel
          right={
            <button
              onClick={() => navigate('/citizen/briefing')}
              className="text-xs font-semibold hover-elevate rounded-md px-1.5 py-0.5"
              style={{ color: 'var(--primary)' }}
            >
              See all <ChevronRight className="w-3 h-3 inline -mt-0.5" />
            </button>
          }
        >
          What's happening
        </SectionLabel>
        <div className="space-y-2.5">
          {feed.length === 0 && (
            <p className="text-sm text-[color:var(--muted-foreground)] px-1 py-4">
              Nothing new right now — check back soon.
            </p>
          )}
          {feed.map((f) => (
            <FeedRow key={f.key} f={f} />
          ))}
        </div>
      </div>

      {/* 5. What you care about — priority chips */}
      <div className="px-4 pt-6 pb-24">
        <SectionLabel
          right={
            <button
              onClick={() => navigate('/citizen/profile')}
              className="text-xs font-semibold hover-elevate rounded-md px-1.5 py-0.5"
              style={{ color: 'var(--primary)' }}
            >
              Change <ChevronRight className="w-3 h-3 inline -mt-0.5" />
            </button>
          }
        >
          What you care about
        </SectionLabel>
        <div className="flex flex-wrap gap-2">
          {priorities.map((p) => (
            <button
              key={p}
              onClick={() => navigate('/citizen/chat')}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold border hover-elevate active-elevate-2"
              style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--cool-2)' }} />
              {p}
            </button>
          ))}
          <button
            onClick={() => navigate('/citizen/profile')}
            className="inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-sm font-semibold border-dashed border-2 hover-elevate active-elevate-2"
            style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
      </div>
    </div>
  );
}

// Unified feed — outreach requests + this week's hearings on real calendar data
function buildFeed({
  outreach,
  events,
  navigate,
}: {
  outreach: OutreachItem[];
  events: CalendarEvent[];
  navigate: (path: string) => void;
}): FeedItem[] {
  const items: FeedItem[] = [];

  outreach.forEach((r) => {
    const expires = r.closes_at || r.end_date;
    const days = expires ? daysUntil(expires) : 30;
    items.push({
      key: `req-${r.id}`,
      icon: <PenSquare className="w-5 h-5" />,
      tone: 'cool',
      label: (r.type || '').toLowerCase() === 'rsvp' ? 'RSVP' : 'Quick survey',
      title: r.name,
      sub: r.template_name ? `From ${r.template_name}` : 'Your voice is requested',
      sort: days,
      onTap: () => navigate(`/citizen/requests/${r.id}`),
    });
  });

  events.slice(0, 6).forEach((e) => {
    const days = daysUntil(e.reading_date);
    const firstBill = e.bills?.[0];
    items.push({
      key: `event-${e.calendar_id}`,
      icon: e.committee_flag ? <Building2 className="w-5 h-5" /> : <Calendar className="w-5 h-5" />,
      tone: 'primary',
      label: `${e.committee_flag ? 'Hearing' : 'Session'} · ${fmtDateMed(e.reading_date)}`,
      title: e.reading_title,
      sub: firstBill?.title ? `Includes ${firstBill.number}` : e.derived_location || e.chamber || 'Colorado legislature',
      sort: days,
      onTap: () => (firstBill?.id ? navigate(`/citizen/bill/${firstBill.id}`) : navigate('/citizen/briefing')),
    });
  });

  return items.sort((a, b) => a.sort - b.sort).slice(0, 5);
}

function FeedRow({ f }: { f: FeedItem }) {
  const tones = {
    cool: { bg: 'var(--cool-3)', fg: '#1F3A6A' },
    primary: { bg: 'var(--accent)', fg: 'var(--primary)' },
    sage: { bg: '#DDEDD9', fg: '#2D5A2D' },
  }[f.tone];

  return (
    <button
      onClick={f.onTap}
      className="w-full p-3 rounded-2xl flex items-center gap-3 hover-elevate active-elevate-2 border text-left"
      style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: tones.bg, color: tones.fg }}
      >
        {f.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: tones.fg }}>
          {f.label}
        </p>
        <p className="text-sm font-bold mt-0.5 leading-tight">{f.title}</p>
        <p className="text-xs text-[color:var(--muted-foreground)] mt-0.5">{f.sub}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-[color:var(--muted-foreground)] shrink-0" />
    </button>
  );
}

function FeaturedRequest({ r, onClick }: { r: OutreachItem; onClick: () => void }) {
  const expires = r.closes_at || r.end_date;
  const dl = expires ? daysUntil(expires) : null;
  return (
    <button
      onClick={onClick}
      className="w-full rounded-2xl p-5 text-left hover-elevate active-elevate-2 relative overflow-hidden"
      style={{ background: 'var(--cool-3)' }}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 font-bold text-sm"
          style={{ background: 'var(--hero-deep-3)', color: 'white' }}
        >
          <PenSquare className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#1F3A6A' }}>
              Your voice is needed
            </span>
            {dl !== null && (
              <span
                className="text-[10px] font-bold uppercase tracking-widest"
                style={{ color: dl <= 3 ? '#C24A4A' : '#1F3A6A' }}
              >
                · {dl} day{dl === 1 ? '' : 's'} left
              </span>
            )}
          </div>
          <h3 className="text-lg font-extrabold leading-tight" style={{ color: '#1F3A6A' }}>
            {r.name}
          </h3>
          {r.description && (
            <p className="text-sm mt-1 leading-snug" style={{ color: '#3D5275' }}>
              {r.description}
            </p>
          )}
          <div className="mt-3 inline-flex items-center gap-1 text-sm font-bold" style={{ color: '#1F3A6A' }}>
            Help out <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </button>
  );
}

type CircleTone = 'accent' | 'dark' | 'cool' | 'plum';

function CircleTile({
  tone,
  icon,
  avatar,
  label,
  badge,
  onClick,
}: {
  tone: CircleTone;
  icon?: React.ReactNode;
  avatar?: string;
  label: string;
  badge?: string;
  onClick: () => void;
}) {
  const tones: Record<CircleTone, { bg: string; fg: string; ring: string }> = {
    accent: { bg: 'var(--accent)', fg: 'var(--primary)', ring: 'rgba(92,58,115,0.18)' },
    dark: { bg: 'var(--hero-deep)', fg: 'var(--hero-light-accent)', ring: 'rgba(42,24,50,0.35)' },
    cool: { bg: 'var(--cool-3)', fg: '#1F3A6A', ring: 'rgba(128,147,174,0.28)' },
    plum: { bg: 'var(--hero-light-accent-2)', fg: 'white', ring: 'rgba(139,103,162,0.35)' },
  };
  const t = tones[tone];
  return (
    <button onClick={onClick} className="relative flex flex-col items-center gap-2 group">
      <div
        className="relative w-[68px] h-[68px] rounded-full flex items-center justify-center transition-transform group-active:scale-90 group-hover:-translate-y-1"
        style={{ background: t.bg, color: t.fg, boxShadow: `0 0 0 1px ${t.ring}, 0 8px 20px -8px ${t.ring}` }}
      >
        {avatar ? <img src={avatar} alt="" className="w-full h-full object-cover rounded-full" /> : icon}
        {badge && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-[10px] font-extrabold tabular-nums"
            style={{ background: 'var(--primary)', color: 'white', boxShadow: '0 0 0 2px var(--background)' }}
          >
            {badge}
          </span>
        )}
      </div>
      <span className="text-[11px] font-bold text-[color:var(--foreground)]">{label}</span>
    </button>
  );
}
